import { useCallback, useRef, useState } from 'react';

// Undo/redo for the autosave edit forms. Each step records a single field
// change with its previous and next value, so undo/redo just re-applies the
// opposite value through the same autosave path.
//
// The stack is persisted to localStorage, per document, so it survives a reload
// or a closed tab — the same arrangement as the portal's
// usePersistedUndoRedoStack. It is *personal*: another admin editing the same
// record has their own stack, and neither sees the other's. The shared,
// server-side record of what changed is the document's `history` field.
//
// Simpler than the portal's version in two ways: steps here hold plain field
// values rather than a Firestore `updateObj`, so there are no sentinels to
// revive on read; and staleness is checked once when the stack is loaded rather
// than on every undo (see `loadFor`).

const STORAGE_VERSION = 1;

export interface UndoStep {
  /** The field key that changed. */
  field: string;
  prevValue: unknown;
  nextValue: unknown;
  /**
   * Messages for the two directions, e.g. "Changed name to 'Tea Ceremony'".
   * Rendered when the step is recorded rather than held as a formatter, so the
   * step is plain JSON and can be persisted.
   */
  undoMessage: string;
  redoMessage: string;
}

interface UndoState {
  stack: UndoStep[];
  /** Index of the last applied step; -1 means nothing to undo. */
  index: number;
}

/** Applies a field value; supplied by the page (updates form + autosaves). */
export type ApplyChange = (field: string, value: unknown, userAction: string) => void;

export interface UndoRedoStack {
  canUndo: boolean;
  canRedo: boolean;
  /** What Undo would do, if anything — e.g. "Changed name to 'Tea Ceremony'". */
  undoLabel: string | undefined;
  /** What Redo would do, if anything. */
  redoLabel: string | undefined;
  steps: UndoStep[];
  index: number;
  record: (step: UndoStep) => void;
  undo: (apply: ApplyChange) => void;
  redo: (apply: ApplyChange) => void;
  /**
   * Points the stack at a document and restores that document's persisted
   * steps. Call it once the document has loaded, passing a reader for its
   * current field values — see the staleness check below.
   */
  loadFor: (storageKey: string | undefined, getCurrentValue: (field: string) => unknown) => void;
}

const EMPTY: UndoState = { stack: [], index: -1 };

/** localStorage key for one document's stack. Mirrors the portal's naming. */
export function undoRedoStorageKey(kind: string, id: string | undefined): string | undefined {
  return id ? `${kind}_${id}_undoRedoStack` : undefined;
}

/** Field values here are scalars, null, or string arrays — no deep compare needed. */
function valuesEqual(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((item, i) => Object.is(item, b[i]));
  }
  return Object.is(a, b);
}

function readStack(storageKey: string | undefined): UndoState {
  if (!storageKey) return EMPTY;
  const raw = localStorage.getItem(storageKey);
  if (!raw) return EMPTY;
  try {
    const parsed = JSON.parse(raw) as UndoState & { version?: number };
    // A stack written by an older shape can't be replayed safely, so drop it
    // rather than guess. Bump STORAGE_VERSION whenever UndoStep changes.
    if (parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.stack)) {
      localStorage.removeItem(storageKey);
      return EMPTY;
    }
    return { stack: parsed.stack, index: parsed.index };
  } catch {
    console.warn(`Discarding unreadable undo stack at ${storageKey}`);
    localStorage.removeItem(storageKey);
    return EMPTY;
  }
}

function writeStack(storageKey: string | undefined, state: UndoState): void {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify({ ...state, version: STORAGE_VERSION }));
  } catch (error) {
    // Out of quota, or storage blocked. Undo still works for this session.
    console.warn('Could not persist the undo stack', error);
  }
}

export function useUndoRedoStack(): UndoRedoStack {
  const [state, setState] = useState<UndoState>(EMPTY);
  // Mirror for synchronous reads inside the imperative handlers below, kept in
  // step by `commit` so two calls in one tick don't race React's batching.
  const stateRef = useRef(state);
  const storageKeyRef = useRef<string | undefined>(undefined);

  const commit = useCallback((next: UndoState) => {
    stateRef.current = next;
    setState(next);
    writeStack(storageKeyRef.current, next);
  }, []);

  const canUndo = state.index >= 0;
  const canRedo = state.index < state.stack.length - 1;

  const record = useCallback(
    (step: UndoStep) => {
      const s = stateRef.current;
      // Drop any redo branch, then append the new step.
      const truncated = s.stack.slice(0, s.index + 1);
      commit({ stack: [...truncated, step], index: truncated.length });
    },
    [commit],
  );

  const undo = useCallback(
    (apply: ApplyChange) => {
      const s = stateRef.current;
      if (s.index < 0) return;
      const step = s.stack[s.index];
      apply(step.field, step.prevValue, `Undo: ${step.undoMessage}`);
      commit({ ...s, index: s.index - 1 });
    },
    [commit],
  );

  const redo = useCallback(
    (apply: ApplyChange) => {
      const s = stateRef.current;
      if (s.index >= s.stack.length - 1) return;
      const step = s.stack[s.index + 1];
      apply(step.field, step.nextValue, `Redo: ${step.redoMessage}`);
      commit({ ...s, index: s.index + 1 });
    },
    [commit],
  );

  const loadFor = useCallback((storageKey: string | undefined, getCurrentValue: (field: string) => unknown) => {
    storageKeyRef.current = storageKey;
    const loaded = readStack(storageKey);

    // A persisted stack can outlive the state it was built against — a
    // colleague edits the same record, or you edit it from another browser.
    // Undoing then would silently overwrite their work with a value from your
    // last session. If the document no longer looks like what the newest step
    // produced, throw the stack away rather than offer a misleading undo.
    if (loaded.index >= 0) {
      const newest = loaded.stack[loaded.index];
      if (!valuesEqual(getCurrentValue(newest.field), newest.nextValue)) {
        if (storageKey) localStorage.removeItem(storageKey);
        stateRef.current = EMPTY;
        setState(EMPTY);
        return;
      }
    }

    stateRef.current = loaded;
    setState(loaded);
  }, []);

  return {
    canUndo,
    canRedo,
    // Described in terms of the value each button would apply, so the tooltip
    // says what you are about to get rather than what you already did.
    undoLabel: canUndo ? state.stack[state.index].undoMessage : undefined,
    redoLabel: canRedo ? state.stack[state.index + 1].redoMessage : undefined,
    steps: state.stack,
    index: state.index,
    record,
    undo,
    redo,
    loadFor,
  };
}
